import { Button, type ButtonProps } from "@/components/ui/button";
import { analytics, type ReorderSource } from "@/lib/analytics";
import { handleActionError, RETRY_HINT } from "@/lib/errors";
import {
  normalizeReorderLines,
  summarize,
  summarizeSelection,
  type LineSelection
} from "@/lib/reorder-diff";
import { useReorderConfirm, useReorderPreview } from "@/queries/reorder";
import type { StoreReorderResponse } from "@repo/api-sdk";
import { toast, Tooltip } from "@medusajs/ui";
import { useNavigate } from "@tanstack/react-router";
import { ShoppingCart, type LucideIcon } from "lucide-react";
import { memo, useCallback, useState } from "react";

import { ReorderReviewDialog } from "./ReorderReviewDialog";

/**
 * The purple, circled look shared by every reorder action on the site (home
 * Highlights and the orders list). Callers add their own size.
 */
export const REORDER_BUTTON_TONE_CLASS =
  "rounded-full border border-purple-200 bg-white text-purple-600 shadow-sm hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700 dark:border-purple-800 dark:bg-neutral-900 dark:text-purple-400 dark:hover:bg-purple-900/30 disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:shadow-none dark:disabled:border-neutral-700 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-500";

interface ReorderButtonProps {
  orderId: string;
  seller?: { id: string; name: string } | null;
  /** Currency for rendering prices in the review dialog (from the order). */
  currencyCode: string;
  /** Which surface rendered this button, for the reorder-decision event. */
  source: ReorderSource;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  label?: string;
  /** Leading icon. Defaults to the shopping cart. */
  icon?: LucideIcon;
  /** Render just the icon, with `label` as the accessible name and tooltip. */
  iconOnly?: boolean;
}

/**
 * "Reorder / buy again" action. On click it previews the reorder (resolve
 * without mutating) and opens {@link ReorderReviewDialog}; confirming adds the
 * available + repriced lines to the seller cart, emits the `reorder_confirmed`
 * telemetry event, and takes the buyer to their cart.
 */
export const ReorderButton = memo(function ReorderButton({
  orderId,
  seller,
  currencyCode,
  source,
  variant = "default",
  size = "default",
  className,
  label = "Reorder",
  icon: Icon = ShoppingCart,
  iconOnly = false
}: ReorderButtonProps) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<StoreReorderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = useReorderPreview();
  const confirm = useReorderConfirm();
  const navigate = useNavigate();

  const handleClick = useCallback(async () => {
    setError(null);
    try {
      const res = await preview.mutateAsync(orderId);
      setResult(res);
      setOpen(true);
    } catch (err) {
      toast.error("Could not prepare your reorder", {
        description: handleActionError(err, {
          boundary: "reorder preview",
          fallback: RETRY_HINT
        })
      });
    }
  }, [orderId, preview]);

  const confirmReorder = useCallback(
    async (selections: LineSelection[], quickBuy: boolean) => {
      setError(null);
      try {
        const res = await confirm.mutateAsync({ orderId, selections });
        const counts = summarize(normalizeReorderLines(res.lines));

        // Compare what the buyer submitted against the original preview to
        // capture the "why" (ADR-0003/0004): how much of what was offered did
        // they actually take, and how much did they adjust.
        const selected = new Set(selections.map((s) => s.variant_id));
        const quantities = Object.fromEntries(
          selections.map((s) => [s.variant_id, s.quantity])
        );
        const selectionCounts = summarizeSelection(
          result ? normalizeReorderLines(result.lines) : [],
          selected,
          quantities
        );

        analytics.reorderConfirmed({
          order_id: orderId,
          seller_id: res.seller?.id ?? seller?.id ?? null,
          added_count: counts.added,
          repriced_count: counts.repriced,
          unavailable_count: counts.unavailable,
          deselected_count: selectionCounts.deselectedCount,
          quantity_overridden_count: selectionCounts.overriddenCount,
          source,
          quick_buy: quickBuy
        });

        setOpen(false);

        if (counts.added > 0 && quickBuy && res.cart_id) {
          // Quick buy skips the cart page, so no "Added to cart" toast - the
          // navigation is the feedback.
          navigate({
            to: "/checkout",
            search: { cart_ids: res.cart_id, step: "information" }
          });
        } else if (counts.added > 0) {
          toast.success(
            `Added ${counts.added} ${counts.added === 1 ? "item" : "items"} to your cart`
          );
          navigate({ to: "/cart" });
        } else {
          toast.info("Nothing was available to add to your cart");
        }
      } catch (err) {
        setError(
          handleActionError(err, {
            boundary: "reorder confirm",
            fallback:
              "We couldn't add these items to your cart. Please try again."
          })
        );
      }
    },
    [orderId, seller, source, result, confirm, navigate]
  );

  const handleConfirm = useCallback(
    (selections: LineSelection[]) => confirmReorder(selections, false),
    [confirmReorder]
  );

  const handleQuickBuy = useCallback(
    (selections: LineSelection[]) => confirmReorder(selections, true),
    [confirmReorder]
  );

  const trigger = (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      aria-label={iconOnly ? label : undefined}
      onClick={handleClick}
      disabled={preview.isPending}
    >
      <Icon
        className={iconOnly ? "h-4 w-4" : "mr-2 h-4 w-4"}
        aria-hidden="true"
      />
      {!iconOnly && (preview.isPending ? "Preparing…" : label)}
    </Button>
  );

  return (
    <>
      {iconOnly ? (
        <Tooltip content={label} side="top">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}

      <ReorderReviewDialog
        open={open}
        onOpenChange={setOpen}
        lines={result ? normalizeReorderLines(result.lines) : []}
        sellerName={result?.seller?.name}
        currencyCode={currencyCode}
        isConfirming={confirm.isPending}
        error={error}
        onConfirm={handleConfirm}
        onQuickBuy={handleQuickBuy}
      />
    </>
  );
});
